/**
 * React Query hooks for Source operations.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import sourceService from '../services/source.service';

interface UseSourceListQueryParams {
  workspaceId?: string;
  idToken: string | undefined;
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useSourceListQuery = ({
  workspaceId,
  idToken,
  page = 1,
  size = 50,
  sortBy = 'name',
  order = 'asc',
  search,
  enabled = true,
  refetchInterval = 5000,
}: UseSourceListQueryParams) => {
  return useQuery({
    queryKey: ['sources', workspaceId, page, size, sortBy, order, search],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }

      return await sourceService.listSources(workspaceId, idToken, {
        page,
        size,
        sortBy,
        order,
        search,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    refetchInterval,
    refetchIntervalInBackground: true,
    staleTime: 2000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseSourceDetailQueryParams {
  sourceId: string | undefined;
  workspaceId?: string;
  idToken: string | undefined;
  enabled?: boolean;
}

export const useSourceDetailQuery = ({
  sourceId,
  workspaceId,
  idToken,
  enabled = true,
}: UseSourceDetailQueryParams) => {
  return useQuery({
    queryKey: ['source', workspaceId, sourceId],
    queryFn: async () => {
      if (!sourceId || !workspaceId || !idToken) {
        throw new Error('Source ID, workspace ID, and token are required');
      }

      return await sourceService.getSource(workspaceId, sourceId, idToken);
    },
    enabled: enabled && !!workspaceId && !!sourceId && !!idToken,
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });
};
